import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bed, Layers, Pencil, Plus, Trash2, X } from "lucide-react";
import { Button, Input } from "@akuna/ui";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import {
  useCreateHotelBed,
  useDeleteHotelBed,
  useDeleteHotelRoom,
  useHotelRooms,
  useSaveRoomCapacity,
  useUpdateHotelBed,
  type HotelRoom,
} from "@/hooks/useHotelRooms";
import { AddTile, SectionCard, SectionHeader, Stepper } from "./primitives";
import { colorDotClass, colorDotStyle } from "./colors";
import RoomDialog from "./RoomDialog";

function BedRoomCard({
  room,
  canCreate,
  canEdit,
  onEdit,
  onDelete,
}: {
  room: HotelRoom;
  canCreate: boolean;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation("daycareHotel");
  const createBed = useCreateHotelBed();
  const deleteBed = useDeleteHotelBed();
  const updateBed = useUpdateHotelBed();
  const [isAdding, setIsAdding] = useState(false);
  const [bedName, setBedName] = useState("");
  const [editingBedId, setEditingBedId] = useState<string | null>(null);
  const [editingBedName, setEditingBedName] = useState("");
  const [pendingBedDelete, setPendingBedDelete] = useState<(typeof room.beds)[number] | null>(null);

  const handleAddBed = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = bedName.trim();
    if (!trimmed) return;
    try {
      await createBed.mutateAsync({ roomId: room.id, name: trimmed, sortOrder: room.beds.length });
      setBedName("");
      setIsAdding(false);
    } catch {
      // hotel_room_beds_name_uniq — most likely cause of a failure here.
      toast({ variant: "destructive", title: t("hotel.toastBedFailed") });
    }
  };

  const handleDeleteBed = async () => {
    if (!pendingBedDelete) return;
    try {
      await deleteBed.mutateAsync(pendingBedDelete.id);
    } catch {
      toast({ variant: "destructive", title: t("hotel.toastBedDeleteFailed") });
    } finally {
      setPendingBedDelete(null);
    }
  };

  const handleSaveBedName = async (event: React.FormEvent, id: string) => {
    event.preventDefault();
    const trimmed = editingBedName.trim();
    if (!trimmed) return;
    try {
      await updateBed.mutateAsync({ id, name: trimmed });
      setEditingBedId(null);
      setEditingBedName("");
    } catch {
      toast({ variant: "destructive", title: t("hotel.toastBedUpdateFailed") });
    }
  };

  return (
    <SectionCard className="overflow-hidden">
      <div className="flex items-center gap-2.5 border-b border-border-gray px-4 py-3.5">
        <span className={cn("h-2.5 w-2.5 shrink-0 rounded-full", colorDotClass(room.color))} style={colorDotStyle(room.color)} aria-hidden="true" />
        <h3 className="min-w-0 flex-1 truncate font-display text-[15px] font-bold text-foreground">{room.name}</h3>
        {canEdit ? (
          <button
            type="button"
            onClick={onEdit}
            aria-label={t("hotel.editRoom", { name: room.name })}
            className="flex h-7 w-7 items-center justify-center rounded-full text-color-light transition-colors hover:bg-background-app hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Pencil className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : null}
        {canEdit ? (
          <button
            type="button"
            onClick={onDelete}
            aria-label={t("hotel.deleteRoom", { name: room.name })}
            className="flex h-7 w-7 items-center justify-center rounded-full text-state-danger transition-colors hover:bg-state-danger-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      <div className="flex items-center gap-1.5 px-4 pb-1 pt-2.5">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-purple-50 px-2.5 py-1 text-[11px] font-semibold text-purple-700">
          <Bed className="h-3.5 w-3.5" aria-hidden="true" />
          {t("hotel.typeBed")}
        </span>
        <span className="text-[11px] font-semibold text-muted-foreground">
          {t("hotel.bedsOccupied", { used: room.occupancy, total: room.beds.length })}
        </span>
      </div>

      <ul>
        {room.beds.map((bed) => (
          <li key={bed.id} className="flex items-center gap-2.5 px-4 py-2">
            {editingBedId === bed.id ? (
              <form onSubmit={(event) => handleSaveBedName(event, bed.id)} className="flex flex-1 items-center gap-2">
                <Input
                  value={editingBedName}
                  onChange={(event) => setEditingBedName(event.target.value)}
                  aria-label={t("hotel.editBed", { name: bed.name })}
                  inputSize="sm"
                  autoFocus
                />
                <Button type="submit" size="sm" disabled={updateBed.isPending}>
                  {t("common.save")}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEditingBedId(null);
                    setEditingBedName("");
                  }}
                >
                  {t("common.cancel")}
                </Button>
              </form>
            ) : (
              <span className="min-w-0 flex-1 truncate text-[13.5px] font-semibold text-foreground">{bed.name}</span>
            )}
            {canEdit && editingBedId !== bed.id ? (
              <button
                type="button"
                onClick={() => {
                  setEditingBedId(bed.id);
                  setEditingBedName(bed.name);
                }}
                aria-label={t("hotel.editBed", { name: bed.name })}
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-color-light transition-colors hover:bg-background-app hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            ) : null}
            {canEdit ? (
              <button
                type="button"
                onClick={() => setPendingBedDelete(bed)}
                aria-label={t("hotel.deleteBed", { name: bed.name })}
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-color-light transition-colors hover:bg-state-danger-soft hover:text-state-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            ) : null}
          </li>
        ))}
      </ul>

      {canCreate ? (
        <div className="px-4 pb-3.5 pt-2.5">
          {isAdding ? (
            <form onSubmit={handleAddBed} className="flex items-center gap-2">
              <Input
                value={bedName}
                onChange={(event) => setBedName(event.target.value)}
                placeholder={t("hotel.bedNamePlaceholder")}
                aria-label={t("hotel.addBed")}
                inputSize="sm"
                autoFocus
                onBlur={() => !bedName.trim() && setIsAdding(false)}
              />
              <Button type="submit" size="sm" disabled={createBed.isPending}>
                {t("common.add")}
              </Button>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => setIsAdding(true)}
              className="w-full rounded-[10px] border-[1.5px] border-dashed border-foreground/[0.16] py-2 text-center text-[12.5px] text-muted-foreground transition-colors hover:bg-background-app focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t("hotel.addBed")}
            </button>
          )}
        </div>
      ) : null}

      <ConfirmationDialog
        open={!!pendingBedDelete}
        onOpenChange={(open) => !open && setPendingBedDelete(null)}
        title={t("hotel.deleteBedTitle")}
        description={
          pendingBedDelete?.occupantName
            ? t("hotel.deleteOccupiedBedDescription", {
                bed: pendingBedDelete.name,
                pet: pendingBedDelete.occupantName,
              })
            : t("hotel.deleteBedDescription", { name: pendingBedDelete?.name ?? "" })
        }
        confirmText={t("hotel.deleteBedConfirm")}
        cancelText={t("common.cancel")}
        variant="destructive"
        onConfirm={handleDeleteBed}
      />
    </SectionCard>
  );
}

function CapacityRoomCard({
  room,
  canEdit,
  onEdit,
  onDelete,
}: {
  room: HotelRoom;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation("daycareHotel");
  const saveCapacity = useSaveRoomCapacity();
  const capacity = room.capacity ?? 0;
  const percent = capacity > 0 ? Math.min(100, Math.round((room.occupancy / capacity) * 100)) : 0;

  return (
    <SectionCard className="p-4">
      <div className="flex items-center gap-2.5">
        <span className={cn("h-2.5 w-2.5 shrink-0 rounded-full", colorDotClass(room.color))} style={colorDotStyle(room.color)} aria-hidden="true" />
        <h3 className="min-w-0 flex-1 truncate font-display text-[15px] font-bold text-foreground">{room.name}</h3>
        {canEdit ? (
          <button
            type="button"
            onClick={onEdit}
            aria-label={t("hotel.editRoom", { name: room.name })}
            className="flex h-7 w-7 items-center justify-center rounded-full text-color-light transition-colors hover:bg-background-app hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Pencil className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : null}
        {canEdit ? (
          <button
            type="button"
            onClick={onDelete}
            aria-label={t("hotel.deleteRoom", { name: room.name })}
            className="flex h-7 w-7 items-center justify-center rounded-full text-state-danger transition-colors hover:bg-state-danger-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      <div className="mt-2.5">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-800">
          <Layers className="h-3.5 w-3.5" aria-hidden="true" />
          {t("hotel.typeCapacity")}
        </span>
      </div>

      <p className="mt-3 font-display text-base font-extrabold text-foreground">
        {t("hotel.animalsOccupied", { used: room.occupancy, total: capacity })}
      </p>
      <div
        className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuenow={room.occupancy}
        aria-valuemin={0}
        aria-valuemax={capacity}
        aria-label={t("hotel.animalsOccupied", { used: room.occupancy, total: capacity })}
      >
        <div
          className={cn("h-full rounded-full transition-all", percent >= 100 ? "bg-state-danger" : "bg-akuna-purple")}
          style={{ width: `${percent}%` }}
        />
      </div>

      <div className="mt-3.5 flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">{t("hotel.capacityLabel")}</span>
        <Stepper
          size="sm"
          min={1}
          value={capacity}
          onChange={(next) => saveCapacity.mutate({ id: room.id, capacity: next })}
          decreaseLabel={t("hotel.decreaseCapacity", { name: room.name })}
          increaseLabel={t("hotel.increaseCapacity", { name: room.name })}
          disabled={!canEdit}
        />
      </div>
    </SectionCard>
  );
}

export default function HotelSpacesSection({
  canEdit,
  canCreate,
}: {
  canEdit: boolean;
  canCreate: boolean;
}) {
  const { t } = useTranslation("daycareHotel");
  const { data: rooms, isLoading } = useHotelRooms();
  const deleteRoom = useDeleteHotelRoom();

  const [editing, setEditing] = useState<HotelRoom | null>(null);
  const [isRoomDialogOpen, setIsRoomDialogOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<HotelRoom | null>(null);

  const bedTotals = useMemo(() => {
    const bedRooms = (rooms ?? []).filter((room) => room.spaceType === "bed");
    return {
      used: bedRooms.reduce((sum, room) => sum + room.occupancy, 0),
      total: bedRooms.reduce((sum, room) => sum + room.beds.length, 0),
    };
  }, [rooms]);

  const openCreate = () => {
    setEditing(null);
    setIsRoomDialogOpen(true);
  };
  const openEdit = (room: HotelRoom) => {
    setEditing(room);
    setIsRoomDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteRoom.mutateAsync(pendingDelete.id);
      toast({ title: t("hotel.toastDeleted") });
    } catch {
      toast({ variant: "destructive", title: t("hotel.toastDeleteFailed") });
    } finally {
      setPendingDelete(null);
    }
  };

  return (
    <section>
      <SectionHeader
        tone="hotel"
        badge={t("badgeHotel")}
        title={t("hotel.title")}
        description={
          <>
            {t("hotel.description")}{" "}
            {bedTotals.total > 0 ? t("hotel.bedsOccupiedToday", { used: bedTotals.used, total: bedTotals.total }) : null}
          </>
        }
        action={
          canCreate ? (
            <Button variant="outline" size="sm" onClick={openCreate}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              {t("hotel.newRoom")}
            </Button>
          ) : undefined
        }
      />

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[0, 1, 2].map((index) => (
            <SectionCard key={index} className="h-[200px] animate-pulse bg-muted/40" />
          ))}
        </div>
      ) : (
        <div className="grid items-start gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {(rooms ?? []).map((room) =>
            room.spaceType === "bed" ? (
              <BedRoomCard
                key={room.id}
                room={room}
                canCreate={canCreate}
                canEdit={canEdit}
                onEdit={() => openEdit(room)}
                onDelete={() => setPendingDelete(room)}
              />
            ) : (
              <CapacityRoomCard
                key={room.id}
                room={room}
                canEdit={canEdit}
                onEdit={() => openEdit(room)}
                onDelete={() => setPendingDelete(room)}
              />
            ),
          )}
          {canCreate ? (
            <AddTile
              onClick={openCreate}
              tone="hotel"
              minHeight="180px"
              icon={<Plus className="h-5 w-5" aria-hidden="true" />}
              title={t("hotel.newRoom")}
            >
              <span className="text-xs text-muted-foreground">{t("hotel.newRoomHint")}</span>
            </AddTile>
          ) : null}
        </div>
      )}

      <RoomDialog open={isRoomDialogOpen} onOpenChange={setIsRoomDialogOpen} room={editing} />
      <ConfirmationDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={t("hotel.deleteTitle")}
        description={t("hotel.deleteDescription", { name: pendingDelete?.name ?? "" })}
        confirmText={t("hotel.deleteConfirm")}
        cancelText={t("common.cancel")}
        variant="destructive"
        onConfirm={handleDelete}
      />
    </section>
  );
}
